import { BaseApiService, ApiResponse } from './BaseApiService';
import { Store } from './types';

type Pagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type StoreListResponse = ApiResponse<Store[]> & {
  pagination?: Pagination;
};

export class StoreApiService extends BaseApiService {
  constructor() {
    super('/stores');
  }

  async getStores(params?: {
    businessId?: string;
    page?: number;
    limit?: number;
    isActive?: boolean;
    storeType?: string;
    near?: string; // "lat,lng,radiusKm"
  }): Promise<StoreListResponse> {
    // Let Axios build the querystring
    return this.get<Store[]>('', { params }) as unknown as StoreListResponse;
  }

  async getStore(storeId: string): Promise<ApiResponse<Store>> {
    return this.get<Store>(`/${storeId}`);
  }

  async createStore(storeData: {
    businessId: string;
    storeName: string;
    manager: string;
    email: string;
    storePhone?: string;
    storeType?: string;
    latitude: number;
    longitude: number;
    // address can be string or object depending on your backend
    address?: any;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    gpsValidation?: {
      enabled: boolean;
      radius: number;
      strictMode?: boolean;
    };
    operatingHours?: any[];
    allowChipActivation?: boolean;
    allowQRActivation?: boolean;
  }): Promise<ApiResponse<Store>> {
    const payload = {
      ...storeData,
      latitude: Number(storeData.latitude),
      longitude: Number(storeData.longitude),
      storeType: storeData.storeType || 'retailer',
      gpsValidation: {
        enabled: true,
        radius: 100,
        strictMode: false,
        ...storeData.gpsValidation,
      },
      // Optional: send GeoJSON explicitly (backend can ignore if already derives it)
      location: {
        type: 'Point',
        coordinates: [Number(storeData.longitude), Number(storeData.latitude)],
      },
    };
    return this.post<Store>('', payload);
  }

  async updateStore(storeId: string, storeData: {
    storeName?: string;
    manager?: string;
    email?: string;
    storePhone?: string;
    address?: any;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    gpsValidation?: {
      enabled: boolean;
      radius: number;
      strictMode?: boolean;
    };
    isActive?: boolean;
    allowChipActivation?: boolean;
    allowQRActivation?: boolean;
  }): Promise<ApiResponse<Store>> {
    const payload = {
      ...storeData,
      ...(storeData.latitude != null && storeData.longitude != null
        ? {
            location: {
              type: 'Point',
              coordinates: [Number(storeData.longitude), Number(storeData.latitude)],
            },
          }
        : {}),
    };
    return this.put<Store>(`/${storeId}`, payload);
  }

  // PATCH /api/stores/:storeId/gps-settings
  async updateGPSSettings(storeId: string, gpsSettings: {
    enabled: boolean;
    radius: number;
    strictMode?: boolean;
  }): Promise<ApiResponse<Store>> {
    return this.patch<Store>(`/${storeId}/gps-settings`, gpsSettings);
  }

  // POST /api/stores/:storeId/validate-activation
  async validateActivation(storeId: string, activationData: {
    userLatitude: number;
    userLongitude: number;
    activationType?: 'chip' | 'qr';
  }): Promise<ApiResponse<{
    allowed: boolean;
    reason?: string;
    distanceMeters?: number;
  }>> {
    return this.post(`/${storeId}/validate-activation`, activationData);
  }

  // GET /api/stores/:storeId/activation-stats
  async getActivationStats(storeId: string): Promise<ApiResponse<{
    totalActivations: number;
    totalScans: number;
    fraudAlerts: number;
    todayActivations: number;
    byType?: Record<string, number>;
  }>> {
    return this.get(`/${storeId}/activation-stats`);
  }

  // GET /api/stores/nearby/:lat/:lng?radius=...&businessId=...
  async getNearbyStores(
    lat: number,
    lng: number,
    params?: { radius?: number; businessId?: string }
  ): Promise<ApiResponse<Store[]>> {
    return this.get<Store[]>(
      `/nearby/${lat}/${lng}`,
      { params: { radius: params?.radius, businessId: params?.businessId } }
    );
  }

  // GET /api/stores/activation-ready/:lat/:lng?businessId=...
  async getActivationReadyStores(
    lat: number,
    lng: number,
    businessId: string
  ): Promise<ApiResponse<Store[]>> {
    return this.get<Store[]>(
      `/activation-ready/${lat}/${lng}`,
      { params: { businessId } }
    );
  }

  // Soft delete (isActive: false)
  async deleteStore(storeId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete<{ deleted: boolean }>(`/${storeId}`);
  }

  // Legacy alias
  async getStoreStats(storeId: string): Promise<ApiResponse<any>> {
    return this.getActivationStats(storeId);
  }

  // Convenience
  async getStoresByLocation(lat: number, lng: number, radiusKm: number = 10): Promise<StoreListResponse> {
    return this.getStores({ near: `${lat},${lng},${radiusKm}` });
  }

  async activateStore(storeId: string): Promise<ApiResponse<Store>> {
    return this.updateStore(storeId, { isActive: true });
  }

  async deactivateStore(storeId: string): Promise<ApiResponse<Store>> {
    return this.updateStore(storeId, { isActive: false });
  }

  async updateChipActivation(storeId: string, enabled: boolean): Promise<ApiResponse<Store>> {
    return this.updateStore(storeId, { allowChipActivation: enabled });
  }

  async updateQRActivation(storeId: string, enabled: boolean): Promise<ApiResponse<Store>> {
    return this.updateStore(storeId, { allowQRActivation: enabled });
  }
}
